import React from "react";
import { Alert, Card, CardContent, CardHeader, Stack } from "@mui/material";
import { importPayPalTransactions, previewPayPalImport } from "../../api";
import FileUpload from "../shared/FileUpload";
import PayPalImportPreview from "./paypal/PayPalImportPreview";
import PayPalImportSummary from "./paypal/PayPalImportSummary";
import {
    getLineNumber,
    isBankCredit,
    isRowSelectable,
} from "./paypal/paypalUtils";

const PayPalImportPanel = () => {
    const [selectedFile, setSelectedFile] = React.useState(null);
    const [previewData, setPreviewData] = React.useState(null);
    const [previewWarnings, setPreviewWarnings] = React.useState([]);
    const [hideBankCredits, setHideBankCredits] = React.useState(false);
    const [selection, setSelection] = React.useState(null);
    const [importResult, setImportResult] = React.useState(null);
    const [importError, setImportError] = React.useState('');
    const [loadingPreview, setLoadingPreview] = React.useState(false);
    const [loadingImport, setLoadingImport] = React.useState(false);

    const resetState = () => {
        setPreviewData(null);
        setPreviewWarnings([]);
        setSelection(null);
        setImportResult(null);
        setImportError('');
    };

    const handleFileSelect = (file) => {
        if (!file) {
            setSelectedFile(null);
            return;
        }
        setSelectedFile(file);
        resetState();
    };

    const handlePreview = async () => {
        if (!selectedFile) {
            setImportError('Bitte eine CSV-Datei auswaehlen.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        setLoadingPreview(true);
        setImportError('');
        setImportResult(null);
        setPreviewWarnings([]);
        setPreviewData(null);
        try {
            const res = await previewPayPalImport(formData);
            if (res.status >= 200 && res.status < 300) {
                const payload = res.data && typeof res.data === 'object' ? res.data : null;
                setPreviewData(payload);
                setPreviewWarnings(Array.isArray(payload?.warnings) ? payload.warnings : []);
                const rows = Array.isArray(payload?.rows) ? payload.rows : [];
                const nextSelection = new Set();
                rows.forEach((row, index) => {
                    if (!isRowSelectable(row)) {
                        return;
                    }
                    if (hideBankCredits && isBankCredit(row?.type)) {
                        return;
                    }
                    nextSelection.add(getLineNumber(row, index));
                });
                setSelection(nextSelection);
            } else {
                const message = typeof res.data === 'string' && res.data.trim().length > 0
                    ? res.data
                    : 'Vorschau fehlgeschlagen.';
                setImportError(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : 'Vorschau fehlgeschlagen.';
            setImportError(message);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleImport = async () => {
        if (!selectedFile) {
            setImportError('Bitte eine CSV-Datei auswaehlen.');
            return;
        }
        if (previewData && selection && selection.size === 0) {
            setImportError('Keine Datensaetze ausgewaehlt.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (selection && selection.size > 0) {
            formData.append('selection', JSON.stringify({ lineNumbers: Array.from(selection) }));
        }
        setLoadingImport(true);
        setImportError('');
        setImportResult(null);
        try {
            const res = await importPayPalTransactions(formData);
            if (res.status >= 200 && res.status < 300) {
                const payload = res.data && typeof res.data === 'object' ? res.data : null;
                setImportResult(payload);
            } else {
                const message = typeof res.data === 'string' && res.data.trim().length > 0
                    ? res.data
                    : 'Import fehlgeschlagen.';
                setImportError(message);
            }
        } catch (error) {
            const backendMessage = error?.response?.data;
            const message = typeof backendMessage === 'string' && backendMessage.trim().length > 0
                ? backendMessage
                : 'Import fehlgeschlagen.';
            setImportError(message);
        } finally {
            setLoadingImport(false);
        }
    };

    const rows = Array.isArray(previewData?.rows) ? previewData.rows : [];
    const visibleRows = rows.filter((row) => !hideBankCredits || !isBankCredit(row?.type));
    const selectableCount = visibleRows.filter((row) => isRowSelectable(row)).length;
    const selectedCount = selection ? selection.size : 0;
    const allSelected = selection && selectableCount > 0 && selectedCount === selectableCount;
    const someSelected = selection && selectedCount > 0 && selectedCount < selectableCount;
    const selectionEmpty = selection && selectedCount === 0;

    const handleToggleHideBankCredits = (event) => {
        const nextValue = event.target.checked;
        setHideBankCredits(nextValue);
        if (!nextValue || !selection) {
            return;
        }
        setSelection((prev) => {
            if (!prev) {
                return prev;
            }
            const next = new Set(prev);
            rows.forEach((row, index) => {
                if (isBankCredit(row?.type)) {
                    next.delete(getLineNumber(row, index));
                }
            });
            return next;
        });
    };

    const handleToggleAll = () => {
        if (!selection) {
            return;
        }
        if (allSelected) {
            setSelection(new Set());
            return;
        }
        const nextSelection = new Set();
        visibleRows.forEach((row, index) => {
            if (isRowSelectable(row)) {
                nextSelection.add(getLineNumber(row, index));
            }
        });
        setSelection(nextSelection);
    };

    const handleToggleRow = (row, index) => {
        if (!selection || !isRowSelectable(row)) {
            return;
        }
        const lineNumber = getLineNumber(row, index);
        setSelection((prev) => {
            if (!prev) {
                return prev;
            }
            const next = new Set(prev);
            if (next.has(lineNumber)) {
                next.delete(lineNumber);
            } else {
                next.add(lineNumber);
            }
            return next;
        });
    };

    return (
        <Card sx={{ borderRadius: 3, boxShadow: "0 8px 28px rgba(0,0,0,0.06)" }}>
            <CardHeader
                title="PayPal (CSV) importieren"
                subheader="CSV-Datei hochladen, Vorschau pruefen und Transaktionen importieren."
            />
            <CardContent>
                <Stack spacing={2}>
                    <FileUpload
                        onFileSelect={handleFileSelect}
                        onPreview={handlePreview}
                        onImport={handleImport}
                        onReset={resetState}
                        accept=".csv"
                        loadingPreview={loadingPreview}
                        loadingImport={loadingImport}
                        selectedFile={selectedFile}
                        importDisabled={!previewData || Boolean(selectionEmpty)}
                    />

                    {importError && (
                        <Alert severity="error">{importError}</Alert>
                    )}

                    {previewWarnings.map((warning, index) => (
                        <Alert key={`paypal-preview-${index}`} severity="warning">
                            {warning}
                        </Alert>
                    ))}

                    {previewData && (
                        <PayPalImportPreview
                            previewData={previewData}
                            rows={rows}
                            visibleRows={visibleRows}
                            hideBankCredits={hideBankCredits}
                            onToggleHideBankCredits={handleToggleHideBankCredits}
                            selection={selection}
                            selectedCount={selectedCount}
                            selectableCount={selectableCount}
                            allSelected={allSelected}
                            someSelected={someSelected}
                            onToggleAll={handleToggleAll}
                            onToggleRow={handleToggleRow}
                        />
                    )}

                    {importResult && (
                        <PayPalImportSummary importResult={importResult} />
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PayPalImportPanel;
